import { ChatWidget } from '@apps/chat-widget';
import { api } from '@packages/backend/convex/_generated/api';
import { signOutApp } from '@packages/cognito-auth/react';
import { createFileRoute, Link, Outlet, useMatches, useParams } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { CircleUserIcon, StoreIcon } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { ActionButton } from '@/components/ActionButton';
import { ModeToggle } from '@/components/ModeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { SuperAdminOnly, useIsSuperAdmin } from '@/features/auth/SuperAdminOnly';
import { useFeatureFlagEnabled } from '@/features/feature-flags/useFeatureFlagEnabled';
import { cn } from '@/lib/cn';
import { isNavLinkVisible } from '@/routes/navLink';
import { RestaurantInfoPopover } from '@/routes/restaurant/RestaurantInfoPopover';
import { restaurantNavLinks } from '@/routes/restaurant/restaurantNavLinks';

export const Route = createFileRoute('/r')({
  component: RestaurantShellLayout,
});

function RestaurantShellLayout() {
  // `strict: false` allows accessing params from child routes (e.g., $restaurantId), not just this layout route
  const { restaurantId } = useParams({ strict: false });
  const restaurantQuery = useQuery(
    api.restaurants.getById,
    restaurantId ? { restaurantId } : 'skip',
  );
  const isSupportChatEnabled = useFeatureFlagEnabled(restaurantId, 'support-chat');
  const isSuperAdmin = useIsSuperAdmin();
  const noPagePadding = useMatches().some((match) => match.staticData.noPagePadding);

  const { data: featureFlags } = useQuery(
    api.featureFlags.listByRestaurantId,
    restaurantId ? { restaurantId } : 'skip',
  );

  // The links point into a restaurant, so `/r`, which lists the restaurants, shows none.
  const navLinks = restaurantId
    ? restaurantNavLinks.filter((navLink) => isNavLinkVisible(navLink, isSuperAdmin, featureFlags))
    : [];

  // `restaurant_id` on every event while in a restaurant, so its logs show on its logs page.
  const posthog = usePostHog();
  useEffect(() => {
    if (!restaurantId) return;
    posthog.register({ restaurant_id: restaurantId });
    return () => posthog.unregister('restaurant_id');
  }, [posthog, restaurantId]);

  return (
    <SidebarProvider defaultOpen={false}>
      {/* Mobile sidebar - only visible on mobile */}
      <Sidebar collapsible="offcanvas" side="left" className="md:hidden">
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Navigation</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navLinks.map((navLink) => (
                  <SidebarMenuItem key={navLink.name}>
                    <SidebarMenuButton asChild>
                      <Link to={navLink.to}>
                        <span>{navLink.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>

      {/* Main content - includes desktop header and page content */}
      <div className="flex min-h-screen w-full flex-col">
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
          {/* Mobile sidebar trigger */}
          <SidebarTrigger className="shrink-0 md:hidden" />

          {/* Desktop horizontal navigation */}
          <nav className="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6">
            <Link className="flex items-center gap-2 text-lg font-semibold md:text-base" to={'/'}>
              <StoreIcon className="h-6 w-6" />
              <span className="sr-only">Home</span>
            </Link>

            {navLinks.map((navLink) => {
              // Super admin links use the super admin orange (as "Admin view" does)
              const [activeClassName, inactiveClassName] = navLink.isSuperAdminOnly
                ? [
                    'text-orange-500 dark:text-orange-400',
                    'text-orange-500/70 hover:text-orange-500 dark:text-orange-400/70 dark:hover:text-orange-400',
                  ]
                : ['text-foreground', 'text-muted-foreground hover:text-foreground'];
              return (
                <Link
                  activeProps={{ className: activeClassName }}
                  className="transition-colors whitespace-nowrap overflow-hidden text-ellipsis"
                  inactiveProps={{ className: inactiveClassName }}
                  key={navLink.name}
                  to={navLink.to}
                >
                  {navLink.name}
                </Link>
              );
            })}
          </nav>

          <div className="flex w-full items-center gap-4 md:ml-auto md:gap-2 lg:gap-4">
            <div className="mr-auto flex-1" />
            <SuperAdminOnly>
              {restaurantQuery.data && <RestaurantInfoPopover restaurant={restaurantQuery.data} />}
            </SuperAdminOnly>
            <ModeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ActionButton className="rounded-full" size="icon" variant="secondary">
                  <CircleUserIcon className="h-5 w-5" />
                  <span className="sr-only">Toggle user menu</span>
                </ActionButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <SuperAdminOnly>
                  <Link className="text-orange-500 dark:text-orange-400" to={'/a'}>
                    <DropdownMenuItem className={'text-orange-500 dark:text-orange-400'}>
                      Admin view
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </Link>
                </SuperAdminOnly>
                <DropdownMenuItem>Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOutApp()}>Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className={cn('flex-1 bg-muted/60 dark:bg-muted/20', !noPagePadding && 'p-6')}>
          <Outlet />
        </main>
        {isSupportChatEnabled && <ChatWidget kind="support" />}
      </div>
    </SidebarProvider>
  );
}
